import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { loadContinueLearning, clearContinueLearning } from '../utils/continueLearning';
// MUI components and icons
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Rating,
  IconButton,
  Collapse,
  Stack,
  Divider,
  Fade,
  Zoom,
  keyframes
} from '@mui/material';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const float2 = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
`;

const float3 = keyframes`
  0%, 100% { transform: translateY(0px) scale(1); }
  50% { transform: translateY(-8px) scale(1.1); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.6), 0 0 30px rgba(139, 92, 246, 0.4); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const wiggle = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
`;

// Motivational quotes for students
const MOTIVATIONAL_QUOTES = [
  { text: "Every expert was once a beginner.", author: "Helen Hayes" },
  { text: "The more you practice, the better you get!", author: "Unknown" },
  { text: "Small progress is still progress.", author: "Unknown" },
  { text: "Believe in yourself and all that you are.", author: "Christian D. Larson" },
  { text: "Success is the sum of small efforts repeated daily.", author: "Robert Collier" },
  { text: "You don't have to be perfect, just keep learning!", author: "Unknown" },
  { text: "Dream big, work hard, stay focused.", author: "Unknown" },
  { text: "Your only limit is your mind.", author: "Unknown" },
];
import VideoCallIcon from '@mui/icons-material/VideoCall';
import RankEmblem from '../components/rank/RankEmblem';
import {
  MenuBook,
  CheckCircle,
  EditNote,
  EmojiEvents,
  Search as SearchIcon,
  School,
  Public as PublicIcon,
  Calculate,
  Language,
  MenuBook as MenuBookIcon,
  Star,
  ExpandMore,
  LocalFireDepartment,
  Bolt,
  TipsAndUpdates,
  AutoAwesome,
  PlayArrow,
  Timer,
  Close as CloseIcon,
  HistoryEdu
} from '@mui/icons-material';

// Cute animated mascot owl component
const MascotOwl = ({ size = 200 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      position: 'relative',
      animation: `${float} 3s ease-in-out infinite`,
    }}
  >
    {/* Owl body */}
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        height: '85%',
        bottom: 0,
        borderRadius: '50% 50% 45% 45%',
        background: 'linear-gradient(180deg, #8B5CF6 0%, #6D28D9 100%)',
        boxShadow: '0 10px 30px rgba(109, 40, 217, 0.3)',
      }}
    />
    {/* Owl face */}
    <Box
      sx={{
        position: 'absolute',
        width: '75%',
        height: '55%',
        top: '20%',
        left: '12.5%',
        borderRadius: '50%',
        background: '#FEF3C7',
      }}
    />
    {/* Eyes */}
    <Box sx={{ position: 'absolute', top: '30%', left: '20%', width: '25%', height: '25%', borderRadius: '50%', background: 'white', boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.1)' }}>
      <Box sx={{ position: 'absolute', top: '30%', left: '30%', width: '40%', height: '40%', borderRadius: '50%', background: '#1F2937' }} />
    </Box>
    <Box sx={{ position: 'absolute', top: '30%', right: '20%', width: '25%', height: '25%', borderRadius: '50%', background: 'white', boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.1)' }}>
      <Box sx={{ position: 'absolute', top: '30%', left: '30%', width: '40%', height: '40%', borderRadius: '50%', background: '#1F2937' }} />
    </Box>
    {/* Beak */}
    <Box
      sx={{
        position: 'absolute',
        top: '48%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '12px solid transparent',
        borderRight: '12px solid transparent',
        borderTop: '18px solid #F59E0B',
      }}
    />
    {/* Graduation cap */}
    <Box
      sx={{
        position: 'absolute',
        top: '-5%',
        left: '50%',
        transform: 'translateX(-50%) rotate(-10deg)',
        width: '70%',
        height: '25%',
      }}
    >
      <Box sx={{ position: 'absolute', bottom: 0, left: '10%', width: '80%', height: '40%', background: '#1F2937', borderRadius: '4px' }} />
      <Box sx={{ position: 'absolute', bottom: '35%', left: '0%', width: '100%', height: '8px', background: '#1F2937' }} />
      <Box sx={{ position: 'absolute', bottom: '35%', right: '-10%', width: '30%', height: '4px', background: '#F59E0B', transform: 'rotate(30deg)', transformOrigin: 'left center' }} />
    </Box>
  </Box>
);

// Daily Challenge Widget
const DailyChallengeWidget = ({ t }) => {
  const challenges = [
    { icon: <Calculate />, text: t('home.dailyChallenge.math', 'Complete 5 math problems'), points: 50 },
    { icon: <MenuBook />, text: t('home.dailyChallenge.reading', 'Read 1 lesson'), points: 30 },
    { icon: <EditNote />, text: t('home.dailyChallenge.practice', 'Practice for 10 minutes'), points: 40 },
  ];

  const [todayChallenge] = useState(() => {
    const dayIndex = new Date().getDate() % challenges.length;
    return challenges[dayIndex];
  });

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2.5,
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.1) 100%)'
          : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        border: theme.palette.mode === 'dark' ? '1px solid rgba(245, 158, 11, 0.25)' : '1px solid #fde68a',
        position: 'relative',
        overflow: 'hidden',
      })}
    >
      <Box sx={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(245, 158, 11, 0.1)', pointerEvents: 'none' }} />
      <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
        <Avatar sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
          width: 48,
          height: 48,
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
        }}>
          {todayChallenge.icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? '#fbbf24' : '#b45309',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5
          })}>
            {t('home.dailyChallenge.title', "Today's Challenge")}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {todayChallenge.text}
          </Typography>
        </Box>
        <Chip
          label={`+${todayChallenge.points} XP`}
          size="small"
          sx={(theme) => ({
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.25)' : '#fef3c7',
            color: theme.palette.mode === 'dark' ? '#fbbf24' : '#b45309',
            fontWeight: 700,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.4)' : '#fde68a',
          })}
        />
      </Stack>
      <Button
        variant="contained"
        fullWidth
        startIcon={<PlayArrow />}
        sx={{
          mt: 2,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
            boxShadow: '0 6px 16px rgba(245, 158, 11, 0.4)',
          }
        }}
        component={Link}
        to="/subjects"
      >
        {t('home.dailyChallenge.start', 'Start Challenge')}
      </Button>
    </Paper>
  );
};

// Motivational Quote Widget
const MotivationalQuote = () => {
  const [quote] = useState(() => {
    const index = Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length);
    return MOTIVATIONAL_QUOTES[index];
  });

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2.5,
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(236, 72, 153, 0.08) 100%)'
          : 'linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)',
        border: theme.palette.mode === 'dark' ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid #e9d5ff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      })}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TipsAndUpdates sx={(theme) => ({ color: theme.palette.mode === 'dark' ? '#c4b5fd' : '#8b5cf6', fontSize: 20 })} />
          <Typography variant="caption" sx={(theme) => ({ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.palette.mode === 'dark' ? '#c4b5fd' : '#7c3aed' })}>
            Daily Inspiration
          </Typography>
        </Stack>
        <Typography variant="body1" fontWeight={500} fontStyle="italic" sx={{ lineHeight: 1.6 }}>
          "{quote.text}"
        </Typography>
        <Typography variant="caption" sx={(theme) => ({ color: theme.palette.mode === 'dark' ? '#a78bfa' : '#7c3aed', fontWeight: 600 })}>
          — {quote.author}
        </Typography>
      </Stack>
    </Paper>
  );
};

// Streak & XP Display Component
const StreakXPDisplay = ({ streak, xp, t }) => (
  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
    <Paper
      elevation={0}
      sx={(theme) => ({
        px: 2,
        py: 1.25,
        borderRadius: 2.5,
        background: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: theme.palette.mode === 'dark' ? '1px solid rgba(239, 68, 68, 0.25)' : '1px solid #fecaca',
      })}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: 2,
        background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
      }}>
        <LocalFireDepartment sx={{ color: '#fff', fontSize: 20 }} />
      </Box>
      <Box>
        <Typography variant="caption" sx={(theme) => ({ fontWeight: 600, color: theme.palette.mode === 'dark' ? '#fca5a5' : '#b91c1c' })}>
          {t('home.streak', 'Streak')}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {streak} {t('home.days', 'days')}
        </Typography>
      </Box>
    </Paper>
    <Paper
      elevation={0}
      sx={(theme) => ({
        px: 2,
        py: 1.25,
        borderRadius: 2.5,
        background: theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: theme.palette.mode === 'dark' ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid #a7f3d0',
      })}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: 2,
        background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      }}>
        <AutoAwesome sx={{ color: '#fff', fontSize: 20 }} />
      </Box>
      <Box>
        <Typography variant="caption" sx={(theme) => ({ fontWeight: 600, color: theme.palette.mode === 'dark' ? '#6ee7b7' : '#047857' })}>
          {t('home.totalXP', 'Total XP')}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {xp.toLocaleString()}
        </Typography>
      </Box>
    </Paper>
  </Stack>
);

// Continue Learning Section Component
const ContinueLearningSection = ({ data, onDismiss, t }) => {
  if (!data || !data.lessonId) return null;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2.5,
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(99, 102, 241, 0.08) 100%)'
          : 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)',
        border: theme.palette.mode === 'dark' ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid #bfdbfe',
      })}
    >
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Avatar sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
            width: 48,
            height: 48,
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
          }}>
            <HistoryEdu sx={{ fontSize: 24 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={(theme) => ({
              color: theme.palette.mode === 'dark' ? '#93c5fd' : '#1d4ed8',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5
            })}>
              {t('home.continueLearning.title', 'Continue Learning')}
            </Typography>
            <Typography variant="body1" fontWeight={600} noWrap title={data.lessonTitle}>
              {data.lessonTitle || t('home.continueLearning.lastViewed', 'Recently viewed')}
            </Typography>
            {data.subjectName && (
              <Typography variant="caption" color="text.secondary">
                {t('home.continueLearning.inSubject', { subject: data.subjectName })}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            component={Link}
            to={`/lesson/${data.lessonId}`}
            startIcon={<PlayArrow />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
            }}
          >
            {t('home.continueLearning.continue', 'Continue')}
          </Button>
          <IconButton
            size="small"
            onClick={onDismiss}
            sx={(theme) => ({
              color: theme.palette.mode === 'dark' ? '#94A3B8' : '#94A3B8',
              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
            })}
            aria-label={t('home.continueLearning.dismiss', 'Dismiss')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');

function HomeLeaderboardPreview() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/leaderboard?limit=10`, { cache: 'no-store' });
        if (!res.ok) throw new Error('Failed');
        const json = await res.json();
        if (mounted) setRows(Array.isArray(json.rows) ? json.rows.slice(0, 10) : []);
      } catch (e) {
        if (mounted) setError(e.message || 'Error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return <Typography color="text.secondary">{t('home.leaderboard.loading','Loading leaderboard...')}</Typography>;
  if (!rows || rows.length === 0) return <Typography color="text.secondary">{t('home.leaderboard.empty','No players yet.')}</Typography>;

  const isEn = i18n.language?.startsWith('en');

  return (
    <Box>
      <Stack spacing={1.0} divider={<Divider flexItem />}>
        {rows.map(r => {
          const username = r.user?.username || (String(r.user?.id||'').length===24 ? t('leaderboard.user','User') : String(r.user?.id||''));
          const letter = String(username).slice(0,1).toUpperCase();
          const rankLabel = isEn ? (r.rank?.nameEn || r.rank?.nameVi) : (r.rank?.nameVi || r.rank?.nameEn);
          return (
            <Box key={`${r.position}-${username}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
              <Box sx={{ width: 28, textAlign: 'right', pr: 1, fontWeight: 700, color: 'text.secondary' }}>#{r.position}</Box>
              <Avatar src={r.user?.avatarUrl || undefined} sx={{ width: 36, height: 36 }}>{letter}</Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" noWrap title={username}>{username}</Typography>
                <Typography variant="caption" color="text.secondary">{rankLabel}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RankEmblem rankKey={r.rank?.key} size={20} title={rankLabel} />
                <Typography variant="body2" color="text.secondary">{r.score}</Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const greeting = user?.name ? t('home.greetingNamed', { name: user.name }) : t('home.greetingDefault');

  const startMeeting = () => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
    navigate(`/call/${id}`);
  };

  // Continue Learning state
  const [continueData, setContinueData] = useState(null);
  useEffect(() => {
    if (user) {
      const data = loadContinueLearning(user.id || user._id);
      setContinueData(data);
    }
  }, [user]);

  const handleDismissContinueLearning = () => {
    if (user) {
      clearContinueLearning(user.id || user._id);
    }
    setContinueData(null);
  };

  // Simple streak/XP logic using localStorage
  const [streak, setStreak] = useState(0);
  const [xp, setXp] = useState(0);
  useEffect(() => {
    try {
      const today = new Date();
      today.setHours(0,0,0,0);
      const last = localStorage.getItem('lastLoginDay');
      const lastXp = parseInt(localStorage.getItem('xp') || '120', 10);
      let newStreak = parseInt(localStorage.getItem('streak') || '0', 10);
      if (last) {
        const lastDate = new Date(parseInt(last, 10));
        lastDate.setHours(0,0,0,0);
        const diff = (today - lastDate) / (1000*60*60*24);
        if (diff === 1) newStreak = newStreak + 1; // consecutive day
        else if (diff > 1) newStreak = 1; // reset
      } else {
        newStreak = 1;
      }
      localStorage.setItem('lastLoginDay', String(today.getTime()));
      localStorage.setItem('streak', String(newStreak));
      setStreak(newStreak);
      setXp(Number.isFinite(lastXp) ? lastXp : 120);
    } catch (_) {
      setStreak(1);
      setXp(120);
    }
  }, []);

  // Student feedback (Học sinh nói gì?)
  const [feedbacks, setFeedbacks] = useState([]);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbError, setFbError] = useState(null);
  const [fbSuccess, setFbSuccess] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'info' });
  const [fbForm, setFbForm] = useState({ name: '', className: '', rating: 5, message: '' });
  const [tfOpen, setTfOpen] = useState(true);
  // Default collapse on small screens to keep the initial view compact
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.innerWidth < 600) setTfOpen(false);
    } catch (_) {}
  }, []);


  // Carousel state and behavior for testimonials
  const defaultTestimonials = [
    { _id: 'd1', name: 'Minh Anh', className: '8', message: 'Em đã cải thiện điểm Toán sau 2 tuần học và luyện tập mỗi ngày.', rating: 5 },
    { _id: 'd2', name: 'Quốc Huy', className: '9', message: 'Kế hoạch học tập giúp em chủ động và bớt quên bài.', rating: 5 },
    { _id: 'd3', name: 'Thảo My', className: '7', message: 'Bài tập phản hồi ngay giúp em hiểu lỗi và sửa nhanh.', rating: 5 },
  ];
  const testimonials = (feedbacks && feedbacks.length > 0) ? feedbacks : defaultTestimonials;

  const [slideIndex, setSlideIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [itemsPerView, setItemsPerView] = useState(1);
  const touchStartXRef = React.useRef(null);
  const touchStartYRef = React.useRef(null);
  const mouseStartXRef = React.useRef(null);
  const AUTO_INTERVAL = 5000; // ms
  const [autoProgress, setAutoProgress] = useState(0);
  useEffect(() => {
    if (isHovered || !Array.isArray(testimonials) || testimonials.length <= itemsPerView) { setAutoProgress(0); return; }
    let start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / AUTO_INTERVAL) * 100);
      setAutoProgress(pct);
      if (pct >= 100) start = Date.now();
    };
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [isHovered, testimonials.length, itemsPerView]);

  // Responsive items per view: 1 (mobile) / 2 (tablet) / 3 (desktop)
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w >= 1024) setItemsPerView(3);
      else if (w >= 640) setItemsPerView(2);
      else setItemsPerView(1);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // Auto-advance slides, pause when hovered or not enough items
  useEffect(() => {
    if (!Array.isArray(testimonials) || testimonials.length <= itemsPerView) return;
    if (isHovered) return;
    const id = setInterval(() => {
      setSlideIndex(prev => (prev + 1) % testimonials.length);
    }, AUTO_INTERVAL);
    return () => clearInterval(id);
  }, [testimonials.length, itemsPerView, isHovered]);

  // Reset index when testimonials set changes or itemsPerView changes
  useEffect(() => { setSlideIndex(0); }, [testimonials.length, itemsPerView]);

  const gotoPrev = () => {
    if (!Array.isArray(testimonials) || testimonials.length <= itemsPerView) return;
    setSlideIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const gotoNext = () => {
    if (!Array.isArray(testimonials) || testimonials.length <= itemsPerView) return;
    setSlideIndex(prev => (prev + 1) % testimonials.length);
  };

  // Touch swipe handlers
  const onTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    const startX = touchStartXRef.current;
    const startY = touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    if (startX == null || !e.changedTouches || e.changedTouches.length === 0) return;
    const dx = e.changedTouches[0].clientX - startX;
    const dy = e.changedTouches[0].clientY - (startY ?? e.changedTouches[0].clientY);
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) gotoNext(); else gotoPrev();
    }
  };

  // Mouse drag (basic) for desktop
  const onMouseDown = (e) => { mouseStartXRef.current = e.clientX; };
  const onMouseUp = (e) => {
    if (mouseStartXRef.current == null) return;
    const dx = e.clientX - mouseStartXRef.current;
    mouseStartXRef.current = null;
    if (Math.abs(dx) > 40) { if (dx < 0) gotoNext(); else gotoPrev(); }
  };

  // Keep a ref of previous feedback list for change detection without re-creating effects
  const prevFeedbacksRef = React.useRef([]);

  const fetchPublic = React.useCallback(async () => {
    try {
      setFbError(null);
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
      const url = `${API_BASE}/feedback/public?limit=10&_=${Date.now()}`; // cache-bust to ensure latest
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(t('home.feedback.loadFailed'));
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      // Detect change (by length or IDs order) to minimize re-renders
      const prev = prevFeedbacksRef.current || [];
      const changed = prev.length !== list.length || prev.some((p, i) => p._id !== (list[i] && list[i]._id));
      if (changed) {
        setFeedbacks(list);
        prevFeedbacksRef.current = list;
        if (prev.length && list.length > prev.length) {
          setFbSuccess(t('home.feedback.newApproved'));
          setTimeout(() => setFbSuccess(''), 3000);
        }
      }
    } catch (e) {
      setFbError(t('home.feedback.cannotLoad'));
    }
  }, []);

  useEffect(() => {
    let aborted = false;
    const doInitial = async () => {
      try {
        setFbLoading(true);
        await fetchPublic();
      } finally {
        if (!aborted) setFbLoading(false);
      }
    };
    doInitial();

    // Poll every 25 seconds
    const pollingId = setInterval(fetchPublic, 25000);

    return () => {
      aborted = true;
      clearInterval(pollingId);
    };
  }, [fetchPublic]);

  // Listen for cross-page feedback updates (Admin approval/reject/delete)
  useEffect(() => {
    const onFeedbackUpdated = () => { fetchPublic(); };
    const onStorage = (e) => {
      if (e.key === 'lastFeedbackUpdate') fetchPublic();
    };
    window.addEventListener('feedbackUpdated', onFeedbackUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('feedbackUpdated', onFeedbackUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, [fetchPublic]);

  const submitFeedback = async (e) => {
    e.preventDefault();
    setFbSuccess('');
    setFbError(null);
    const name = (fbForm.name || '').trim();
    const message = (fbForm.message || '').trim();
    const className = (fbForm.className || '').trim();
    const rating = Number(fbForm.rating || 5);
    if (!name || !message) {
      const msg = t('home.feedback.formRequired');
      setFbError(msg);
      setToast({ message: msg, type: 'error' });
      return;
    }
    // Validate optional class: must be 1-12 if provided
    if (className) {
      const num = Number(className);
      if (!Number.isFinite(num) || num < 1 || num > 12) {
        const msg = t('home.feedback.classInvalid');
        setFbError(msg);
        setToast({ message: msg, type: 'error' });
        return;
      }
    }
    try {
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
      const res = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, className, rating, message })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || t('home.feedback.submitFailed'));
      }
      setFbSuccess(t('home.feedback.thanks'));
      setToast({ message: t('home.feedback.thanks'), type: 'success' });
      setFbForm({ name: '', className: '', rating: 5, message: '' });
      // Do not show unapproved feedback immediately; keep homepage showing only approved ones.
      // Optionally, re-fetch the public list (which returns only approved):
      try {
        const API_BASE2 = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
        const res2 = await fetch(`${API_BASE2}/feedback/public?limit=10`);
        if (res2.ok) {
          const data2 = await res2.json();
          setFeedbacks(Array.isArray(data2) ? data2 : []);
        }
      } catch (_) {
        // silent
      }
    } catch (e) {
      const msg = e.message || t('home.feedback.submitFailed');
      setFbError(msg);
      setToast({ message: msg, type: 'error' });
    }
  };

  return (
      <>
      {/* Page header (title + greeting) above main content */}
      {user && (
      <Box component="header" sx={{ mb: 2 }} aria-label={t('common.home')}>
        <Typography variant="h5" sx={{ mt: 0 }}>
          {t('home.welcomeBackHome')}
        </Typography>
      </Box>
      )}
      <Box component="main" sx={{ py: { xs: 1, sm: 1.5 }, width: '100%', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <Paper
        component="section"
        elevation={0}
        aria-labelledby="home-hero-title"
        sx={(theme) => ({
          borderRadius: 3,
          p: { xs: 2.5, md: 4 },
          mb: 4,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 50%, rgba(59, 130, 246, 0.1) 100%)'
            : 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 50%, #eff6ff 100%)',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid #c7d2fe',
          position: 'relative',
          overflow: 'hidden',
        })}
      >
        {/* Floating decorative elements */}
        {[
          { emoji: '📚', top: '10%', right: '5%', size: 28, anim: `${float2} 4s ease-in-out infinite` },
          { emoji: '✨', top: '20%', right: '15%', size: 20, anim: `${sparkle} 2s ease-in-out infinite` },
          { emoji: '🎯', bottom: '15%', right: '8%', size: 24, anim: `${float3} 5s ease-in-out infinite 0.5s` },
          { emoji: '💡', top: '60%', right: '20%', size: 22, anim: `${float} 3.5s ease-in-out infinite 1s` },
          { emoji: '🌟', top: '5%', right: '25%', size: 18, anim: `${sparkle} 2.5s ease-in-out infinite 0.3s` },
        ].map((item, idx) => (
          <Box
            key={idx}
            sx={{
              position: 'absolute',
              top: item.top,
              right: item.right,
              bottom: item.bottom,
              fontSize: item.size,
              animation: item.anim,
              pointerEvents: 'none',
              opacity: 0.7,
              display: { xs: 'none', sm: 'block' },
              zIndex: 0,
            }}
          >
            {item.emoji}
          </Box>
        ))}
        {/* Decorative blur circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, borderRadius: '50%', background: 'rgba(139, 92, 246, 0.15)', filter: 'blur(40px)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: '30%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(59, 130, 246, 0.12)', filter: 'blur(30px)', pointerEvents: 'none' }} />

        <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={8}>
            <Typography id="home-hero-title" variant="h4" fontWeight={700} gutterBottom>
              {t('home.hero.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {greeting} {t('home.hero.subtitleAfterGreeting')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }} aria-label={t('home.hero.metricsAria')}>
              <Chip size="small" label={`+500 ${t('home.hero.metrics.lessons')}`} sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe', color: theme.palette.mode === 'dark' ? '#93c5fd' : '#1d4ed8', fontWeight: 600 })} />
              <Chip size="small" label={`+1000 ${t('home.hero.metrics.exercises')}`} sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.2)' : '#ede9fe', color: theme.palette.mode === 'dark' ? '#c4b5fd' : '#6d28d9', fontWeight: 600 })} />
              <Chip size="small" label={`24/7 ${t('home.hero.metrics.access')}`} sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5', color: theme.palette.mode === 'dark' ? '#6ee7b7' : '#047857', fontWeight: 600 })} />
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 2.5 }}>
              <Button
                component={Link}
                to="/subjects"
                variant="contained"
                startIcon={<MenuBookIcon />}
                aria-label={t('home.hero.exploreAria')}
                sx={{
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  py: 1.2,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.5)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                {t('home.hero.explore')}
              </Button>
              {user ? (
                <>
                  <Button component={Link} to="/planning" variant="outlined" startIcon={<EditNote />} aria-label={t('home.hero.planningAria')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    {t('home.hero.planning')}
                  </Button>
                  <Button onClick={startMeeting} variant="outlined" startIcon={<VideoCallIcon />} aria-label="Start a meeting" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Start a meeting
                  </Button>
                </>
              ) : (
                <Button component={Link} to="/register" variant="outlined" startIcon={<EditNote />} aria-label={t('home.hero.registerAria')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                  {t('home.hero.getStarted')}
                </Button>
              )}
            </Stack>
            <StreakXPDisplay streak={streak} xp={xp} t={t} />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <MascotOwl size={160} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Continue Learning Section - Only for logged in users with recent activity */}
      {user && continueData && (
        <Fade in timeout={400}>
          <Box sx={{ mb: 4 }}>
            <ContinueLearningSection
              data={continueData}
              onDismiss={handleDismissContinueLearning}
              t={t}
            />
          </Box>
        </Fade>
      )}

      {/* Daily Challenge & Motivation Section - Only for logged in users */}
      {user && (
        <Fade in timeout={500}>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={7}>
              <DailyChallengeWidget t={t} />
            </Grid>
            <Grid item xs={12} md={5}>
              <MotivationalQuote />
            </Grid>
          </Grid>
        </Fade>
      )}

      {/* Features Section with MUI Cards */}
      <Box component="section" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>{t('home.features.title')}</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          {t('home.features.subtitle')}
        </Typography>
        <Grid container spacing={2}>
          {[
            { icon: <MenuBook />, title: t('home.features.byTopic.title'), desc: t('home.features.byTopic.desc'), color: '#3b82f6', lightBg: '#eff6ff', darkBg: 'rgba(59, 130, 246, 0.15)', emoji: '📖' },
            { icon: <CheckCircle />, title: t('home.features.practice.title'), desc: t('home.features.practice.desc'), color: '#10b981', lightBg: '#ecfdf5', darkBg: 'rgba(16, 185, 129, 0.15)', emoji: '✅' },
            { icon: <EditNote />, title: t('home.features.planning.title'), desc: t('home.features.planning.desc'), color: '#8b5cf6', lightBg: '#f5f3ff', darkBg: 'rgba(139, 92, 246, 0.15)', emoji: '📝' },
            { icon: <EmojiEvents />, title: t('home.features.rewards.title'), desc: t('home.features.rewards.desc'), color: '#f59e0b', lightBg: '#fffbeb', darkBg: 'rgba(245, 158, 11, 0.15)', emoji: '🏆' }
          ].map((f, idx) => (
            <Grid item xs={12} sm={6} md={3} key={`feat-${idx}`}>
              <Zoom in style={{ transitionDelay: `${idx * 100}ms` }}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                  p: 2.5,
                  borderRadius: 3,
                  height: '100%',
                  background: theme.palette.mode === 'dark' ? f.darkBg : f.lightBg,
                  border: `1px solid ${theme.palette.mode === 'dark' ? `${f.color}30` : `${f.color}25`}`,
                  transition: 'all 0.25s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 12px 28px ${f.color}25`,
                    borderColor: `${f.color}50`,
                    '& .feature-emoji': {
                      transform: 'scale(1.2) rotate(10deg)',
                      opacity: 0.15,
                    },
                    '& .feature-avatar': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 4px 16px ${f.color}40`,
                    }
                  }
                })}
              >
                {/* Background floating emoji */}
                <Box className="feature-emoji" sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  fontSize: 40,
                  opacity: 0.08,
                  transition: 'all 0.3s ease',
                  pointerEvents: 'none',
                }}>
                  {f.emoji}
                </Box>
                <Avatar
                  className="feature-avatar"
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: `${f.color}20`,
                    mb: 2,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {React.cloneElement(f.icon, { sx: { color: f.color, fontSize: 26 } })}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>{f.title}</Typography>
                <Typography variant="body2" color="text.secondary">{f.desc}</Typography>
              </Paper>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* How It Works Section */}
      <Box
        component="section"
        aria-labelledby="how-title"
        sx={(theme) => ({
          mb: 6,
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(59, 130, 246, 0.08) 50%, rgba(139, 92, 246, 0.08) 100%)'
            : 'linear-gradient(135deg, #ecfdf5 0%, #eff6ff 50%, #f5f3ff 100%)',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(16, 185, 129, 0.15)' : '1px solid #a7f3d0',
        })}
      >
        <Typography id="how-title" variant="h5" fontWeight={700} textAlign="center" gutterBottom>
          {t('home.how.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
          {t('home.how.subtitle', 'Start learning in 3 simple steps')}
        </Typography>
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          {[
            { num: '1', icon: <SearchIcon />, title: t('home.how.step1.title'), desc: t('home.how.step1.desc'), color: '#10b981', emoji: '🔍' },
            { num: '2', icon: <School />, title: t('home.how.step2.title'), desc: t('home.how.step2.desc'), color: '#3b82f6', emoji: '📖' },
            { num: '3', icon: <Star />, title: t('home.how.step3.title'), desc: t('home.how.step3.desc'), color: '#8b5cf6', emoji: '⭐' },
          ].map((step, idx) => (
            <Grid item xs={12} md={4} key={`step-${idx}`} sx={{ display: 'flex' }}>
              <Fade in style={{ transitionDelay: `${idx * 150}ms` }}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                  width: '100%',
                  p: 2.5,
                  borderRadius: 3,
                  border: theme.palette.mode === 'dark' ? `1px solid ${step.color}30` : `1px solid ${step.color}25`,
                  background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 1.5,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.02)',
                    boxShadow: `0 12px 28px ${step.color}25`,
                    '& .step-number': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 6px 20px ${step.color}50`,
                    },
                    '& .step-emoji': {
                      transform: 'scale(1.3) rotate(15deg)',
                      opacity: 0.2,
                    }
                  }
                })}
              >
                {/* Background emoji */}
                <Box className="step-emoji" sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  fontSize: 50,
                  opacity: 0.06,
                  transition: 'all 0.3s ease',
                  pointerEvents: 'none',
                }}>
                  {step.emoji}
                </Box>
                <Box
                  className="step-number"
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}cc 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    fontWeight: 800,
                    color: '#fff',
                    boxShadow: `0 4px 14px ${step.color}40`,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {step.num}
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>{step.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, position: 'relative', zIndex: 1 }}>{step.desc}</Typography>
              </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Popular Subjects Section */}
      <Box component="section" sx={{ mb: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('home.popular.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('home.popular.subtitle', 'Continue where you left off')}</Typography>
          </Box>
          <Button component={Link} to="/subjects" variant="outlined" endIcon={<PlayArrow />} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {t('home.popular.viewAll')}
          </Button>
        </Stack>
        <Grid container spacing={2}>
          {[
            { icon: <Calculate />, title: t('home.popular.math.title'), desc: t('home.popular.math.desc'), progress: 30, lessons: 24, color: '#3b82f6', emoji: '📐' },
            { icon: <MenuBook />, title: t('home.popular.vietnamese.title'), desc: t('home.popular.vietnamese.desc'), progress: 45, lessons: 32, color: '#8b5cf6', emoji: '📝' },
            { icon: <Language />, title: t('home.popular.english.title'), desc: t('home.popular.english.desc'), progress: 20, lessons: 28, color: '#ec4899', emoji: '🌐' }
          ].map((s, i) => (
            <Grid item xs={12} md={4} key={`subj-${i}`}>
              <Zoom in style={{ transitionDelay: `${i * 100}ms` }}>
              <Card
                sx={(theme) => ({
                  borderRadius: 3,
                  border: theme.palette.mode === 'dark' ? `1px solid ${s.color}30` : `1px solid ${s.color}25`,
                  background: theme.palette.mode === 'dark' ? `${s.color}10` : `${s.color}08`,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 28px ${s.color}20`,
                    borderColor: `${s.color}50`,
                    '& .subject-emoji': {
                      transform: 'scale(1.2) rotate(10deg)',
                      opacity: 0.15,
                    }
                  }
                })}
                elevation={0}
              >
                {/* Background emoji */}
                <Box className="subject-emoji" sx={{
                  position: 'absolute',
                  top: 15,
                  right: 15,
                  fontSize: 45,
                  opacity: 0.08,
                  transition: 'all 0.3s ease',
                  pointerEvents: 'none',
                }}>
                  {s.emoji}
                </Box>
                <CardActionArea component={Link} to="/subjects" sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                    <Avatar sx={{
                      width: 48,
                      height: 48,
                      bgcolor: `${s.color}20`,
                      transition: 'all 0.3s ease',
                    }}>
                      {React.cloneElement(s.icon, { sx: { color: s.color, fontSize: 24 } })}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>{s.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.lessons} {t('home.popular.lessons', 'lessons')}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{s.desc}</Typography>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">{t('home.popular.progress', 'Progress')}</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ color: s.color }}>{s.progress}%</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={s.progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: `${s.color}15`,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          background: `linear-gradient(90deg, ${s.color} 0%, ${s.color}cc 100%)`,
                        }
                      }}
                    />
                  </Box>
                </CardActionArea>
              </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>


      {/* Quick Access */}
      <Box component="section" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>{t('home.quick.title')}</Typography>
        <Grid container spacing={2}>
          {[
            { to: '/subjects', title: t('nav.subjects'), icon: <MenuBook />, label: t('home.quick.toSubjectsAria'), color: '#3b82f6', emoji: '📚' },
            { to: '/planning', title: t('nav.planning'), icon: <EditNote />, label: t('home.quick.toPlanningAria'), color: '#8b5cf6', emoji: '📋' },
            { to: user ? '/points' : '/register', title: user ? t('nav.points') : t('common.register'), icon: user ? <EmojiEvents /> : <EditNote />, label: user ? t('home.quick.toPointsAria') : t('home.quick.toRegisterAria'), color: '#f59e0b', emoji: '🏆' },
            { to: '/subjects', title: t('home.popular.viewAll'), icon: <PublicIcon />, label: 'View all', color: '#10b981', emoji: '🌍' }
          ].map((item, idx) => (
            <Grid item xs={6} md={3} key={`quick-${idx}`}>
              <Fade in style={{ transitionDelay: `${idx * 80}ms` }}>
              <Card
                elevation={0}
                sx={(theme) => ({
                  borderRadius: 3,
                  border: theme.palette.mode === 'dark' ? `1px solid ${item.color}30` : `1px solid ${item.color}20`,
                  background: theme.palette.mode === 'dark' ? `${item.color}10` : `${item.color}08`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 20px ${item.color}20`,
                    borderColor: `${item.color}50`,
                    '& .quick-avatar': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 4px 12px ${item.color}40`,
                    }
                  }
                })}
              >
                <CardActionArea component={Link} to={item.to} aria-label={item.label} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar className="quick-avatar" sx={{
                      width: 40,
                      height: 40,
                      bgcolor: `${item.color}20`,
                      transition: 'all 0.3s ease',
                    }}>
                      {React.cloneElement(item.icon, { sx: { fontSize: 20, color: item.color } })}
                    </Avatar>
                    <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                  </Stack>
                </CardActionArea>
              </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA Banner */}
        <Paper
            component="section"
            elevation={0}
            aria-label={t('home.cta.aria')}
            sx={(theme) => ({
              mb: 6,
              borderRadius: 3,
              p: { xs: 2.5, md: 4 },
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(236, 72, 153, 0.1) 100%)'
                : 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 50%, #fdf2f8 100%)',
              border: theme.palette.mode === 'dark' ? '1px solid rgba(139, 92, 246, 0.25)' : '1px solid #ddd6fe',
              position: 'relative',
              overflow: 'hidden',
            })}
        >
        {/* Decorative elements */}
        <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(139, 92, 246, 0.1)', filter: 'blur(30px)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -20, left: '20%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(236, 72, 153, 0.1)', filter: 'blur(25px)', pointerEvents: 'none' }} />
        {/* Floating emojis */}
        <Box sx={{ position: 'absolute', top: 20, right: 30, fontSize: 30, opacity: 0.15, animation: `${float2} 4s ease-in-out infinite`, pointerEvents: 'none', display: { xs: 'none', md: 'block' } }}>🚀</Box>
        <Box sx={{ position: 'absolute', bottom: 20, right: 100, fontSize: 24, opacity: 0.12, animation: `${sparkle} 3s ease-in-out infinite 0.5s`, pointerEvents: 'none', display: { xs: 'none', md: 'block' } }}>✨</Box>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('home.cta.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('home.cta.subtitle')}</Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              component={Link}
              to={user ? '/planning' : '/register'}
              variant="contained"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {user ? t('home.cta.newGoal') : t('home.cta.registerFree')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Leaderboard Preview Section */}
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('home.leaderboard.title', 'Leaderboard')}</Typography>
      <Card
        elevation={0}
        sx={(theme) => ({
          borderRadius: 2,
          mb: 4,
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0',
        })}
      >
          <CardHeader action={
            <Button component={Link} to="/leaderboard" size="small" variant="text">{t('home.leaderboard.viewAll','View full leaderboard')}</Button>
          } />
          <CardContent>
            <HomeLeaderboardPreview />
          </CardContent>
     </Card>

      {/* Testimonials / Student Feedback Section */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          borderRadius: 3,
          mb: 4,
          background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#F8FAFC',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0',
        })}
      >
        {/* Section header */}
        <Box sx={{ px: 3, pt: 3, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('home.testimonials.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
              {t('home.testimonials.subtitle', 'Real stories from real students')}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setTfOpen(prev => !prev)}
            aria-label={tfOpen ? (t('common.collapse') || 'Collapse') : (t('common.expand') || 'Expand')}
            sx={(theme) => ({
              background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
              '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0' },
            })}
          >
            <ExpandMore sx={(theme) => ({ transform: tfOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s', color: theme.palette.mode === 'dark' ? '#94A3B8' : '#64748B' })} />
          </IconButton>
        </Box>

        <Collapse in={tfOpen} timeout="auto">
          <Box sx={{ position: 'relative', zIndex: 1, px: 3, pb: 3 }}>
            <section className="testimonials" aria-labelledby="testimonials-title">
              <h2 id="testimonials-title" style={{ display: 'none' }}>{t('home.testimonials.title')}</h2>

              {/* Carousel */}
              <Box
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                sx={{ position: 'relative', overflow: 'hidden', borderRadius: 3, py: 1, minHeight: 180 }}
                aria-roledescription="carousel"
                aria-label={t('home.testimonials.ariaCarousel')}
              >
                {fbLoading ? (
                  <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', py: 4, textAlign: 'center' }}>{t('home.testimonials.loading')}</Typography>
                ) : (
                  (() => {
                    if (!Array.isArray(testimonials) || testimonials.length === 0) {
                      return <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', py: 4, textAlign: 'center' }}>{t('home.testimonials.empty')}</Typography>;
                    }
                    const visible = [];
                    for (let i = 0; i < Math.min(itemsPerView, testimonials.length); i++) {
                      const idx = (slideIndex + i) % testimonials.length;
                      visible.push(testimonials[idx]);
                    }
                    const cardColors = ['#64748B', '#64748B', '#64748B'];
                    return (
                      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))`, gap: 2 }}>
                        {visible.map((item, k) => {
                          const accent = cardColors[k % cardColors.length];
                          const rating = Math.max(0, Math.min(5, Math.round(Number.isFinite(item.rating) ? item.rating : 5)));
                          return (
                            <Box
                              key={item._id || `t-${slideIndex}-${k}`}
                              sx={{
                                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
                                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.9)',
                                borderRadius: 3, p: 2.5, position: 'relative', overflow: 'hidden',
                                boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.2)' : '0 4px 20px rgba(99,102,241,0.08)',
                                transition: 'transform 0.3s, box-shadow 0.3s',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(99,102,241,0.15)',
                                },
                              }}
                            >
                              {/* Top accent gradient line */}
                              <Box sx={{
                                position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                                background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
                              }} />

                              {/* Mini quote icon */}
                              <Box sx={{
                                position: 'absolute', top: 12, right: 16, fontSize: '32px', fontFamily: 'Georgia, serif',
                                color: isDark ? 'rgba(255,255,255,0.04)' : `${accent}15`, lineHeight: 1,
                              }}>"</Box>

                              {/* Avatar + Info */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Box sx={{
                                  p: '2px', borderRadius: '50%',
                                  background: `linear-gradient(135deg, ${accent}, ${accent}66)`,
                                }}>
                                  <Avatar sx={{
                                    width: 40, height: 40, fontSize: '1rem', fontWeight: 700,
                                    bgcolor: isDark ? '#1e1b4b' : '#fff',
                                    color: accent,
                                    border: '2px solid',
                                    borderColor: isDark ? '#1e1b4b' : '#fff',
                                  }} aria-hidden>
                                    {String(item.name || 'H').charAt(0).toUpperCase()}
                                  </Avatar>
                                </Box>
                                <Box>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: isDark ? '#e0e7ff' : '#1e1b4b', lineHeight: 1.3 }}>
                                    {item.name || t('home.testimonials.anonymous')}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.45)' : '#6366f1', fontWeight: 500 }}>
                                    {item.className ? t('home.testimonials.classLabel', { class: item.className }) : t('home.testimonials.anonymous')}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Message */}
                              <Typography sx={{
                                color: isDark ? 'rgba(255,255,255,0.8)' : '#334155', lineHeight: 1.65, fontSize: '0.92rem',
                                fontStyle: 'italic', mb: 2,
                              }}>
                                "{item.message}"
                              </Typography>

                              {/* Star rating */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {Array.from({ length: rating }).map((_, i) => (
                                  <Box key={`sf-${k}-${i}`} component="span" sx={{ color: '#f59e0b', fontSize: '1rem' }}>★</Box>
                                ))}
                                {Array.from({ length: 5 - rating }).map((_, i) => (
                                  <Box key={`se-${k}-${i}`} component="span" sx={{ color: isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0', fontSize: '1rem' }}>★</Box>
                                ))}
                                <Typography sx={{ ml: 0.5, fontSize: '0.78rem', color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', fontWeight: 600 }}>
                                  {rating}.0
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()
                )}

                {/* Prev/Next controls */}
                {Array.isArray(testimonials) && testimonials.length > itemsPerView && (
                  <>
                    <IconButton
                      aria-label={t('home.testimonials.prevAria')}
                      onClick={gotoPrev}
                      sx={{
                        position: 'absolute', top: '50%', left: 4, transform: 'translateY(-50%)',
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 12px rgba(99,102,241,0.15)',
                        color: isDark ? '#a5b4fc' : '#6366f1', width: 36, height: 36,
                        '&:hover': { background: isDark ? 'rgba(255,255,255,0.12)' : '#fff', transform: 'translateY(-50%) scale(1.1)' },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1 }}>‹</Typography>
                    </IconButton>
                    <IconButton
                      aria-label={t('home.testimonials.nextAria')}
                      onClick={gotoNext}
                      sx={{
                        position: 'absolute', top: '50%', right: 4, transform: 'translateY(-50%)',
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 12px rgba(99,102,241,0.15)',
                        color: isDark ? '#a5b4fc' : '#6366f1', width: 36, height: 36,
                        '&:hover': { background: isDark ? 'rgba(255,255,255,0.12)' : '#fff', transform: 'translateY(-50%) scale(1.1)' },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1 }}>›</Typography>
                    </IconButton>
                  </>
                )}
              </Box>

              {/* Dots + Progress */}
              {Array.isArray(testimonials) && testimonials.length > itemsPerView && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center', mb: 1.5 }} aria-label={t('home.testimonials.dotsAria')}>
                    {testimonials.map((_, i) => (
                      <Box
                        key={`dot-${i}`}
                        component="button"
                        onClick={() => setSlideIndex(i)}
                        aria-label={t('home.testimonials.dotAria', { index: i + 1 })}
                        sx={{
                          width: i === slideIndex ? 24 : 8, height: 8,
                          borderRadius: 4, border: 'none', cursor: 'pointer', p: 0,
                          background: i === slideIndex
                            ? 'linear-gradient(90deg, #6366f1, #8b5cf6)'
                            : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(99,102,241,0.2)',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={autoProgress}
                    sx={{
                      height: 3, borderRadius: 2, mx: 'auto', maxWidth: 200,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(99,102,241,0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 2,
                        background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899)',
                      },
                    }}
                  />
                </Box>
              )}

              {/* Feedback Form */}
              <Box sx={{
                mt: 3, p: 2.5, borderRadius: 3,
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(99,102,241,0.08)',
              }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: isDark ? '#c7d2fe' : '#4338ca' }}>
                  {t('home.feedback.form.aria', 'Share your experience')}
                </Typography>
                <Box component="form" onSubmit={submitFeedback} aria-label={t('home.feedback.form.aria')}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth size="small"
                        label={t('home.feedback.form.namePlaceholder')}
                        value={fbForm.name}
                        onChange={e => setFbForm(v => ({ ...v, name: e.target.value }))}
                        aria-label={t('home.feedback.form.nameAria')}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth size="small"
                        type="number"
                        inputProps={{ min: 1, max: 12 }}
                        label={t('home.feedback.form.classPlaceholder')}
                        value={fbForm.className}
                        onChange={e => {
                          let v = (e.target.value || '').replace(/\D/g, '');
                          if (v === '') { setFbForm(prev => ({ ...prev, className: '' })); return; }
                          let num = parseInt(v, 10);
                          if (!Number.isFinite(num)) { setFbForm(prev => ({ ...prev, className: '' })); return; }
                          if (num < 1) num = 1;
                          if (num > 12) num = 12;
                          setFbForm(prev => ({ ...prev, className: String(num) }));
                        }}
                        aria-label={t('home.feedback.form.classAria')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', mb: 0.3 }}>{t('home.feedback.form.ratingAria')}</Typography>
                        <Rating value={Number(fbForm.rating)} onChange={(_, v) => setFbForm(prev => ({ ...prev, rating: v || 5 }))} size="small" />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small"
                        multiline
                        minRows={2}
                        label={t('home.feedback.form.messagePlaceholder')}
                        value={fbForm.message}
                        onChange={e => setFbForm(v => ({ ...v, message: e.target.value }))}
                        aria-label={t('home.feedback.form.messageAria')}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit" variant="contained" size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3,
                          boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                          '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' },
                        }}
                      >
                        {t('home.feedback.form.submit')}
                      </Button>
                      {fbSuccess && (
                        <Chip color="success" label={`${t('home.feedback.thanks')} 🎉🎉`} size="small" sx={{ ml: 1.5 }} />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </section>
          </Box>
        </Collapse>
      </Paper>
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'info' })} silent />
    </Box>
      </>
  );
}